import { ColorContext, UniqueWidgetRefreshContext } from 'App';
import { ConditionIcons } from 'components';
import Day15Display from 'components/Day15Forecast/Day15Display';
import OvalDisplay from 'components/OvalDisplay';
import { useAPIData } from 'hooks/useAPIData';
import Cookies from 'js-cookie';
import React, { useContext, useEffect, useState } from 'react';
import { IconContext } from 'react-icons';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';
import { get15DayForecast } from 'services/weather/weather.service';
import { Range } from 'utils/mathUtils';
import { DateToString } from 'utils/stringUtils';
import { ErrorMessage } from './ErrorMessage';
import WidgetLoadingAnimation from './WidgetLoadingAnimation';
import WidgetTimeUpdate from './WidgetTimeUpdate';

let dataCache;

export default function DayForecast15Widget({ widgetRefresh, dimensions, containerSizePx }) {
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState();
  const [selected, setSelected] = useState(0);
  const [strDate, setStrDate] = useState();
  const [error, setError] = useState({ state: false, message: '' });

  const FusionData = useAPIData({
    successCallback: () => {
      setFetching(false);
    },
    errorCallback: () => {
      setError({ state: true, message: 'Error fetching data' });
      setFetching(false);
    },
    fetchingCallback: () => {
      setFetching(true);
    },
  });

  const theme = useContext(ColorContext);
  const containerWidth = Number.isFinite(containerSizePx?.width) ? containerSizePx.width : 320;
  const isSingleColumnWidget = dimensions?.[0] === 1;
  const arrowButtonWidth = Math.max(
    30,
    Math.min(50, Math.round(containerWidth * (isSingleColumnWidget ? 0.11 : 0.065))),
  );
  const arrowIconSize = Math.max(14, Math.min(20, Math.round(arrowButtonWidth * 0.42)));
  const compact15Day1x1VisibleCount = containerWidth < 310 ? 2 : 3;
  const compact15Day1x2Columns = containerWidth < 340 ? 2 : containerWidth < 440 ? 3 : 4;
  useEffect(() => {
    dataCache = null;
    setFetching(true);
  }, [widgetRefresh]);

  const uwf = useContext(UniqueWidgetRefreshContext);
  const ROUTE = 'get15DayForecast';
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;

  useEffect(() => {
    const fetchData = async () => {
      try {
        FusionData.forceGet({
          key: 'get15DayForecast',
          apiFunction: get15DayForecast,
        }).then((res) => {
          setFetching(false);
          if (!res?.data?.result || res.data.result.length == 0) return;

          res.data.result.map((item) => {
            // item.loctime = new Date(item.iso8601.split(".00")[0]);
            item.loctime = new Date(item.utc_time);
          });

          dataCache = res.data.result;

          setData(dataCache);

          setStrDate(DateToString(res.data.result[0].loctime));
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [uwf[UNIQUEKEY]]);
  useEffect(() => {
    //get data from api
    (async () => {
      if (dataCache) {
        setData(dataCache);
        setFetching(false);
        return;
      }
      FusionData.get({ key: 'get15DayForecast', apiFunction: get15DayForecast })
        .then((res) => {
          setFetching(false);
          if (!res?.data?.result || res.data.result.length == 0) return;

          res.data.result.map((item) => {
            // item.loctime = new Date(item.iso8601.split(".00")[0]);
            item.loctime = new Date(item.utc_time);
          });

          dataCache = res.data.result;

          setData(dataCache);

          setStrDate(DateToString(res.data.result[0].loctime));
        })
        .catch((err) => {
          setError({ state: true, message: err.message });
        });
    })();
  }, [widgetRefresh]);

  const updateSelected = (newSelected) => {
    setSelected(newSelected);
    setStrDate(DateToString(data[newSelected].loctime));
  };

  const formatTitle = (item) => {
    try {
      const s = DateToString(item.loctime);
      return `${s.weekday_short} ${s.month_short} ${s.day}, ${s.year}`;
    } catch (e) {
      return '';
    }
  };

  if (error.state && !fetching) {
    return (
      <ErrorMessage
        message={error.message}
        retryRoute={ROUTE}
        onRetry={() => {
          setError({ state: false, message: '' });
          setFetching(true);
        }}
      />
    );
  }

  // if (true)
  if (fetching)
    return <WidgetLoadingAnimation widgetName={'15DayForecast'} dimensions={dimensions} />;

  if (data && data.length === 0) {
    return (
      <div>
        <WidgetTimeUpdate route={ROUTE} />
        <div style={{ padding: 15 }}>No data found.</div>
      </div>
    );
  }

  const RightArrowComponent = () => {
    return (
      <button
        type="button"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: arrowButtonWidth,
          minWidth: arrowButtonWidth,
          flex: `0 0 ${arrowButtonWidth}px`,
          border: 'none',
          background: 'transparent',
          padding: 0,
          opacity: selected < data.length - 1 ? 1 : 0,
          cursor: selected < data.length - 1 ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (selected < data.length - 1) updateSelected(selected + 1);
        }}
      >
        <IconContext.Provider
          value={{
            color: theme.fontColor,
            size: arrowIconSize,
          }}
        >
          <FaChevronRight />
        </IconContext.Provider>
      </button>
    );
  };

  const LeftArrowComponent = () => {
    return (
      <button
        type="button"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: arrowButtonWidth,
          minWidth: arrowButtonWidth,
          flex: `0 0 ${arrowButtonWidth}px`,
          border: 'none',
          background: 'transparent',
          padding: 0,
          opacity: selected > 0 ? 1 : 0,
          cursor: selected > 0 ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (selected > 0) updateSelected(selected - 1);
        }}
      >
        <IconContext.Provider
          value={{
            color: theme.fontColor,
            size: arrowIconSize,
          }}
        >
          <FaChevronLeft />
        </IconContext.Provider>
      </button>
    );
  };

  if (dimensions[0] == 2 && dimensions[1] == 1) {
    if (data)
      return (
        <div
          style={{
            height: '100%',
          }}
        >
          <WidgetTimeUpdate route={ROUTE} />
          {data.length && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 10,
                paddingTop: 5,
                height: '90%',
              }}
            >
              {strDate && (
                <div
                  style={{
                    fontSize: 20,
                    borderBottom: 'solid grey 1px',
                    flex: 1,
                  }}
                >
                  {`${strDate.weekday_short} ${strDate.month_short} ${strDate.day}, ${strDate.year}`}
                </div>
              )}
              {/* Oval spreads */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  // justifyContent: "space-between",
                  alignContent: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  overflow: 'hidden',
                  paddingLeft: 10,
                  paddingRight: 10,
                  flex: 15,
                }}
              >
                <LeftArrowComponent />

                {data.map((item, index) => {
                  const length = data.length;
                  const numDisplayed = 7;
                  const halfNum = numDisplayed / 2;

                  if (
                    !Range(
                      Math.max(Math.min(selected, length - halfNum), halfNum) - halfNum,
                      Math.min(Math.max(selected, halfNum), length - halfNum) + halfNum,
                      index,
                    )
                  )
                    return;

                  return (
                    <button
                      key={index}
                      onClick={() => updateSelected(index)}
                      style={{
                        height: '100%',
                      }}
                    >
                      <OvalDisplay
                        key={index}
                        style={
                          {
                            // marginLeft: 15,
                            // height: 275,
                            // margin: 10,
                          }
                        }
                        time={`${item.loctime.getMonth() + 1}/${item.loctime.getDate()}`}
                        temp={
                          <div>
                            <div>{`${parseFloat(item.high_temp).toFixed(0)}°F`}</div>
                            <div>{`${parseFloat(item.low_temp).toFixed(0)}°F`}</div>
                          </div>
                        }
                        icon={<ConditionIcons conditionId={item.icon} />}
                        selected={selected === index}
                      />
                    </button>
                  );
                })}

                <RightArrowComponent />
              </div>

              {/* Day15 display */}
              <Day15Display
                data={data[selected]}
                title={formatTitle(data[selected])}
                hideHeader={true}
              />
            </div>
          )}
        </div>
      );
  }
  if (dimensions[0] == 1 && dimensions[1] == 1) {
    if (data)
      return (
        <div style={{ height: '100%' }}>
          <WidgetTimeUpdate route={ROUTE} />
          {data.length && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                // padding: 15,
                height: '90%',
                padding: 5,
                // background:'red',
              }}
            >
              {/* Oval spreads */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                <LeftArrowComponent />

                {data &&
                  data.map((item, index) => {
                    const length = data.length;
                    const numDisplayed = compact15Day1x1VisibleCount;
                    const halfNum = numDisplayed / 2;

                    if (
                      !Range(
                        Math.max(Math.min(selected, length - halfNum), halfNum) - halfNum,
                        Math.min(Math.max(selected, halfNum), length - halfNum) + halfNum,
                        index,
                      )
                    )
                      return;

                    return (
                      <button
                        type="button"
                        key={index}
                        onClick={() => setSelected(index)}
                        style={{
                          height: '100%',
                          minWidth: 0,
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                        }}
                      >
                        <OvalDisplay
                          key={index}
                          style={
                            {
                              // marginLeft: 15,
                              // height: 275,
                              // margin: 10,
                            }
                          }
                          time={`${item.loctime.getMonth() + 1}/${item.loctime.getDate()}`}
                          temp={
                            <div>
                              <div>{`${parseFloat(item.high_temp).toFixed(0)}°F`}</div>
                              <div>{`${parseFloat(item.low_temp).toFixed(0)}°F`}</div>
                            </div>
                          }
                          icon={<ConditionIcons conditionId={item.icon} />}
                          selected={selected === index}
                        />
                      </button>
                    );
                  })}

                <RightArrowComponent />
              </div>

              {/* Day15 display */}
              <Day15Display
                data={data[selected]}
                title={formatTitle(data[selected])}
                hideHeader={true}
              />
            </div>
          )}
        </div>
      );
  }

  if (dimensions[0] == 1 && dimensions[1] == 2) {
    if (data)
      return (
        <div style={{ height: '100%', width: '100%' }}>
          <WidgetTimeUpdate route={ROUTE} />
          {data.length && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 3,
                paddingTop: 5,
                height: '90%',
                width: '100%',
              }}
            >
              {/* Oval spreads */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0,
                }}
              >
                <LeftArrowComponent />

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${compact15Day1x2Columns}, minmax(0, 1fr))`,
                    gap: 5,
                    height: '100%',
                    width: '90%',
                    // background: "blue",
                  }}
                >
                  {data &&
                    data.map((item, index) => {
                      const length = data.length;
                      const numDisplayed = compact15Day1x2Columns * 2;
                      const halfNum = numDisplayed / 2;

                      if (
                        !Range(
                          Math.max(Math.min(selected, length - halfNum), halfNum) - halfNum,
                          Math.min(Math.max(selected, halfNum), length - halfNum) + halfNum,
                          index,
                        )
                      )
                        return;

                      return (
                        <button
                          type="button"
                          key={index}
                          onClick={() => setSelected(index)}
                          style={{
                            height: '100%',
                            width: '100%',
                            minWidth: 0,
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                          }}
                        >
                          <OvalDisplay
                            key={index}
                            style={
                              {
                                // height: 275,
                                // margin: 5,
                              }
                            }
                            time={`${item.loctime.getMonth() + 1}/${item.loctime.getDate()}`}
                            temp={
                              <div>
                                <div>{`${parseFloat(item.high_temp).toFixed(0)}°F`}</div>
                                <div>{`${parseFloat(item.low_temp).toFixed(0)}°F`}</div>
                              </div>
                            }
                            icon={<ConditionIcons conditionId={item.icon} />}
                            selected={selected === index}
                          />
                        </button>
                      );
                    })}
                </div>

                <RightArrowComponent />
              </div>

              {/* Day15 display */}
              <Day15Display
                data={data[selected]}
                title={formatTitle(data[selected])}
                hideHeader={true}
              />
            </div>
          )}
        </div>
      );
  }

  return (
    <div>
      <WidgetTimeUpdate updateIntervalMinutes={60} />
      <div style={{ padding: 15 }}>No data found.</div>
    </div>
  );
}
